import { LandingPageContent } from '../types';

/**
 * Second pillar page, for the brand-adjacent head term ("personalized gifts
 * for kids"). Sits alongside /gifts-for-kids: that page owns the generic
 * gifting query, this one owns the personalization-intent query. The two
 * interlink but target distinct titles and angles to avoid cannibalising
 * each other.
 */
const personalizedGiftsForKids: LandingPageContent = {
	path: '/personalized-gifts-for-kids',
	seoTitle: 'Personalized Gifts for Kids — Custom Books & Keepsakes With Their Name',
	metaDescription:
		'Personalized gifts for kids: storybooks starring your child as the illustrated hero, custom coloring books, t-shirts and keepsakes with their name. You approve every page before print.',
	heading: 'Personalized Gifts for Kids',
	subheading: 'Not their name on a product. Their face in the story.',
	heroImage: '/assets/hero/kid-4/flat_scene.png',
	intro: `"Personalized" is doing a lot of heavy lifting in gift shops these days. Most of what carries the label is a mass-produced item with a name printed on it — monogramming, really.

We mean something more literal. You upload a photo; we illustrate your child — recognisably *them* — and build them into a printed storybook, a coloring book, or a keepsake where they are the subject of the artwork, not a label on it.

This page covers what genuine personalization looks like, who it suits, and how to pick well — including when you're buying for a niece, nephew or grandchild you don't see every day.`,
	sections: [
		{
			heading: 'The difference between a name and a likeness',
			body: `A water bottle with "Emma" on it is a way of not losing the water bottle. A book where Emma — her hair, her smile, her actual face rendered in watercolour — rescues a dragon is a different category of object.

The test is simple: does the child return to it a month later? Name-printed items get used; likeness-based gifts get *kept*. Children go back to them the way adults go back to photo albums, because that is roughly what they are — a photo album where the photos are adventures.

Three things make it work:

1. **The child appears in the artwork**, not just the text.
2. **The likeness is consistent** from page to page — children notice a changing face faster than adults do.
3. **It's physical.** Printed, on a shelf, handable to a grandparent.`,
		},
		{
			heading: 'What you can personalize',
			body: `**Storybooks.** The flagship. Your child illustrated as the hero across a full printed story, in the art style you choose — watercolour, classic storybook, flat modern, or a Pixar-like 3D look. Best for ages 2–8.

**Coloring books.** Outline drawings of your child ready to be finished in felt-tip. The child completes the artwork themselves, which children find quietly thrilling. Best for ages 3–7.

**T-shirts, mugs and posters.** The illustrated character, printed on things that get used daily. These age better than storybooks for children of nine and up.

**A book with their name woven through it.** If what you searched for was "book with my child's name in it" — yes, the name appears throughout the story. But here it comes attached to their face, which is the part a child actually reacts to.`,
		},
		{
			heading: 'Buying for a child you don’t live with',
			body: `Aunts, uncles, godparents and grandparents are half of our orders, and personalized gifts suit them for a practical reason: they signal attention across distance.

What you need is one clear photo — a phone snap from the parents is plenty — and the child's name. That's it. The gift then does something a toy cannot: it proves you know exactly who they are.

For grandparents in another country, a book in their own language, starring their grandchild, travels better than anything else we know of. Stories can be produced in several languages, including Greek and Arabic with proper right-to-left layout.`,
		},
		{
			heading: 'How it works, start to finish',
			body: `1. **Upload a photo.** Clear, well-lit, face visible. A phone photo is fine.
2. **Choose a story and an art style.** The avatar is generated and drawn consistently into every page.
3. **Approve the pages.** Nothing is printed until you have seen every page and said yes. If a page isn't right, it gets regenerated.
4. **Printed and shipped.** Real paper, real binding, to your door — or straight to the child's door with a gift note.`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description: 'The flagship — your child as the illustrated hero of a printed story.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description: 'Outline pages starring your child, ready for felt-tip.',
		},
		{
			label: 'Custom kids t-shirts',
			href: '/custom-kids-t-shirts',
			description: 'The illustrated character on something worn weekly.',
		},
		{
			label: 'Browse everything',
			href: '/products',
			description: 'Books, prints, mugs, posters, puzzles and more.',
		},
	],
	faqs: [
		{
			question: 'What is the best personalized gift for a child?',
			answer:
				'For ages 2–8, a personalized storybook where the child appears as the illustrated main character. It works as both a gift and a keepsake, and children return to it repeatedly. For ages 9 and up, printed keepsakes — custom t-shirts, mugs or posters of their illustrated character — age better.',
		},
		{
			question: 'What is the difference between personalized and monogrammed gifts?',
			answer:
				'Monogramming puts a name on a mass-produced item. Genuine personalization makes the child the subject: their likeness in the artwork, their name in the story, consistent from page to page. Children respond to seeing their face far more strongly than to seeing their name.',
		},
		{
			question: 'Can I order a personalized gift for a child I don’t have photos of?',
			answer:
				'You need one clear photo, which the parents can send from a phone. The face should be visible, in focus and reasonably well lit — nothing professional is required.',
		},
		{
			question: 'Is a personalized book a good gift from grandparents?',
			answer:
				'It is one of the strongest. It signals attention across distance, and it can be produced in the grandparents’ own language — including Greek and Arabic — so the gift carries the family language with it.',
		},
		{
			question: 'Do I get to see the gift before it is printed?',
			answer:
				'Yes. You review and approve every page before anything goes to print. Pages you are not happy with can be regenerated.',
		},
	],
	related: [
		{ label: 'Gifts for kids', href: '/gifts-for-kids' },
		{ label: 'Christmas gifts for kids', href: '/gifts/christmas-gifts-for-kids' },
		{ label: 'Christening gifts', href: '/gifts/christening-gifts' },
		{ label: 'Name day gift ideas', href: '/blog/name-day-gift-ideas' },
	],
	cta: { label: 'Browse personalized gifts', href: '/products' },
};

export default personalizedGiftsForKids;
